import React from 'react';
import {
 Input
} from 'shared/components/ui/input';
import {
 Label
} from 'shared/components/ui/label';
import {
 Checkbox
} from 'shared/components/ui/checkbox';
import {
 TooltipProvider, Tooltip, TooltipTrigger, TooltipContent
} from 'shared/components/ui/tooltip';
import {
 Info
} from 'lucide-react';

interface ConnectionSettings {
    uri?: string;
    api_key?: string;
    token?: string;
    alias?: string;
    connection_timeout?: number;
    secure?: boolean;
}

interface VectorSearchDbFieldsProps {
    type: 'elastic' | 'milvus';
    connection: { settings?: ConnectionSettings };
}

const inputStyles = 'focus-visible:ring-neutral-950 dark:focus-visible:ring-neutral-300';
const checkboxStyles =
    'data-[state=checked]:bg-neutral-950 data-[state=checked]:text-neutral-50 dark:data-[state=checked]:bg-neutral-50 dark:data-[state=checked]:text-neutral-950';
const tooltipIconStyles = 'inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800';

const tooltips = {
    elastic_uri: 'The full URI of your Elasticsearch cluster, e.g., https://my-cluster.es.us-east-1.aws.elastic.co:9243',
    elastic_api_key: 'The API key for authenticating with Elasticsearch.',
    milvus_uri: 'The URI of your Milvus instance, e.g., https://my-milvus.cloud.zilliz.com',
    milvus_token: 'The token for authenticating with Milvus.',
    alias: 'A friendly alias for this connection (used for internal reference).',
    connection_timeout: 'The time (in seconds) to wait for a connection before timing out.',
    secure: 'Enable secure (TLS/SSL) connection to the server.',
};

export const VectorSearchDbFields: React.FC<VectorSearchDbFieldsProps> = ({
 type, connection
}) => {
    const settings = connection?.settings || {};

    return (
        <TooltipProvider>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {/* URI */}
                <div className="space-y-2 md:col-span-2">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Label htmlFor="uri" className="select-none text-xs">
                                {type === 'elastic' ? 'Elasticsearch URI' : 'Milvus URI'}
                                <Info className={tooltipIconStyles} />
                            </Label>
                        </TooltipTrigger>
                        <TooltipContent className="w-[300px]">
                            <p>{type === 'elastic' ? tooltips.elastic_uri : tooltips.milvus_uri}</p>
                        </TooltipContent>
                    </Tooltip>
                    <Input
                        id="uri"
                        name="uri"
                        required
                        placeholder={type === 'elastic'
                            ? 'https://my-cluster.es.us-east-1.aws.elastic.co:9243'
                            : 'https://my-milvus.cloud.zilliz.com'}
                        defaultValue={settings.uri}
                        className={inputStyles}
                    />
                </div>

                {/* API Key (Elasticsearch) / Token (Milvus) */}
                <div className="space-y-2">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Label
                                htmlFor={type === 'elastic' ? 'api_key' : 'token'}
                                className="select-none text-xs"
                            >
                                {type === 'elastic' ? 'API Key' : 'Token'}
                                <Info className={tooltipIconStyles} />
                            </Label>
                        </TooltipTrigger>
                        <TooltipContent className="w-[300px]">
                            <p>{type === 'elastic' ? tooltips.elastic_api_key : tooltips.milvus_token}</p>
                        </TooltipContent>
                    </Tooltip>
                    <Input
                        id={type === 'elastic' ? 'api_key' : 'token'}
                        name={type === 'elastic' ? 'api_key' : 'token'}
                        type="password"
                        required
                        defaultValue={type === 'elastic' ? settings.api_key : settings.token}
                        className={inputStyles}
                    />
                </div>

                {/* Alias */}
                <div className="space-y-2">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Label htmlFor="alias" className="select-none text-xs">
                                Alias (Optional)
                                <Info className={tooltipIconStyles} />
                            </Label>
                        </TooltipTrigger>
                        <TooltipContent className="w-[300px]">
                            <p>{tooltips.alias}</p>
                        </TooltipContent>
                    </Tooltip>
                    <Input
                        id="alias"
                        name="alias"
                        placeholder="e.g., prod-search"
                        defaultValue={settings.alias}
                        className={inputStyles}
                    />
                </div>

                {/* Connection Timeout */}
                <div className="space-y-2">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Label htmlFor="connection_timeout" className="select-none text-xs">
                                Connection Timeout (seconds)
                                <Info className={tooltipIconStyles} />
                            </Label>
                        </TooltipTrigger>
                        <TooltipContent className="w-[300px]">
                            <p>{tooltips.connection_timeout}</p>
                        </TooltipContent>
                    </Tooltip>
                    <Input
                        id="connection_timeout"
                        name="connection_timeout"
                        type="number"
                        defaultValue={settings.connection_timeout || 30}
                        className={inputStyles}
                    />
                </div>

                {/* Secure Connection (Milvus only) */}
                {type === 'milvus' && (
                    <div className="flex items-center space-x-2 pt-6 md:col-span-2">
                        <Checkbox
                            id="secure"
                            name="secure"
                            className={checkboxStyles}
                            defaultChecked={Boolean(settings.secure)}
                        />
                        <Label htmlFor="secure" className="font-normal">
                            Enable Secure Connection (TLS/SSL)
                        </Label>
                    </div>
                )}
            </div>
        </TooltipProvider>
    );
};
